import React from 'react';
import { motion } from 'framer-motion';
import { FaGraduationCap } from 'react-icons/fa';
import { useInView } from '../hooks/useInView';
import { fadeIn, fadeInUp, staggerContainer } from '../utils/animations';
import { useTranslation } from 'react-i18next';



const Education: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isInView } = useInView(0.1);



  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  const educationData = [
    {
      degree: t('education.degrees.bachelor'),
      school: t('education.schools.highSchool'),
      location: 'Sousse, Tunisia',
      period: t('education.periods.bachelor'),
      description: t('education.descriptions.bachelor'),
      achievements: t('education.achievements.bachelor', { returnObjects: true }) as string[]
    },
    {
      degree: t('education.degrees.bachelorDegree'),
      school: t('education.schools.epi'),
      location: 'Sousse, Tunisia',
      period: t('education.periods.bachelorDegree'),
      description: t('education.descriptions.bachelorDegree'),
      achievements: t('education.achievements.bachelorDegree', { returnObjects: true }) as string[]
    },
    {
      degree: t('education.degrees.engineering'),
      school: t('education.schools.polytechnic'),
      location: 'Sousse, Tunisia',
      period: t('education.periods.engineering'),
      description: t('education.descriptions.engineering'),
      achievements: t('education.achievements.engineering', { returnObjects: true }) as string[]
    }
  ];

  return (
    <motion.section
      id="education"
      className="py-24 relative"
      ref={ref as any}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeIn}
    >
      <div className="absolute top-1/3 right-0 w-1/4 h-1/4 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-5"></div>
      <div className="absolute bottom-1/3 left-0 w-1/4 h-1/4 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-5"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-16" variants={fadeInUp}>
          <motion.span
            className="inline-block px-3 py-1 text-sm font-medium bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-lg rounded-full text-gray-300 mb-3"
            variants={fadeInUp}
          >
            {t('education.academicBackground')}
          </motion.span>
          <motion.h2
            className="text-3xl md:text-4xl font-fancy mb-4 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500"
            variants={fadeInUp}
          >
            {t('education.title')}
          </motion.h2>
          <motion.div
            className="h-1 w-20 bg-gradient-to-r from-blue-400 to-purple-500 mx-auto rounded-full mb-8"
            variants={fadeInUp}
          ></motion.div>
          <motion.p
            className="text-gray-300 text-lg max-w-3xl mx-auto"
            variants={fadeInUp}
          >
            {t('education.description')}
          </motion.p>
        </motion.div>

        <div className="relative">
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-700 hidden md:block"></div>
          <motion.div className="space-y-0" variants={staggerContainer}>
            {educationData.map((edu, index) => (
              <motion.div
                key={index}
                className={`mb-16 flex ${index % 2 === 0 ? 'md:justify-start' : 'md:justify-end'} justify-center`}
                variants={itemVariants}
              >
                <motion.div 
                  className="relative"
                  whileHover={{ y: -5 }}
                >
                  <motion.div
                    className="bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-lg rounded-lg shadow-xl p-6 md:p-8 max-w-md border border-gray-700 hover:border-blue-500 transition-all duration-300 mt-3"
                  >
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-blue-500 rounded-full border-4 border-gray-900 z-10"></div>
                    <div className="flex items-center mb-4">
                      <div className="p-3 bg-blue-500 bg-opacity-20 rounded-full mr-4">
                        <FaGraduationCap className="text-blue-400 text-2xl" />
                      </div>
                      <h3 className="text-xl font-bold text-white">{edu.degree}</h3>
                    </div>

                    <div className="mb-4 pl-2 border-l-2 border-gray-700">
                      <div className="flex items-center mb-2">
                        <span className="text-blue-400 font-medium">{edu.school}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-400">
                        <span>{edu.location}</span>
                        <span>{edu.period}</span>
                      </div>
                    </div>

                    <p className="text-gray-300 mb-4">{edu.description}</p>

                    <h4 className="text-white font-medium mb-2">{t('education.keyAchievements')}</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      {edu.achievements.map((achievement, i) => (
                        <li key={i} className="text-gray-300 text-sm">{achievement}</li>
                      ))}
                    </ul>
                  </motion.div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default Education;
